import React from 'react';
import { Check, X } from 'lucide-react';
import { TECHNICIANS, parseTechnicians, formatTechnicians } from '../services/crmBackendService';
import { Label } from './ui/label';

/**
 * TechnicianMultiSelect
 * Allows assigning one or more technicians to a job/estimate.
 *
 * Props:
 * - value: string (comma-separated email list, e.g. "tech1@aaairrigationservice.com, tech2@aaairrigationservice.com")
 *          or array of strings
 * - onChange: function(newValueString) - called with the updated comma-separated string
 * - label: optional custom label (defaults to "Assigned Technicians")
 * - disabled: optional boolean
 * - className: optional CSS class
 */
export default function TechnicianMultiSelect({
  value = '',
  onChange,
  label = 'Assigned Technicians',
  disabled = false,
  className = '',
}) {
  const selected = parseTechnicians(value);

  const handleToggle = (techValue) => {
    if (disabled) return;
    const next = selected.includes(techValue)
      ? selected.filter((v) => v !== techValue)
      : [...selected, techValue];
    if (onChange) {
      onChange(formatTechnicians(next));
    }
  };

  const handleClear = () => {
    if (disabled) return;
    if (onChange) {
      onChange('');
    }
  };

  return (
    <div className={`space-y-1.5 ${className}`}>
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium text-gray-700">{label}</Label>
        {selected.length > 0 && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="text-[11px] text-gray-400 hover:text-gray-600 flex items-center gap-0.5 transition-colors"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {TECHNICIANS.map((t) => {
          const isSelected = selected.includes(t.value);
          return (
            <button
              key={t.value}
              type="button"
              disabled={disabled}
              aria-pressed={isSelected}
              onClick={() => handleToggle(t.value)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium border flex items-center gap-1.5 transition-all select-none ${
                isSelected
                  ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <span
                className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center text-[10px] transition-colors ${
                  isSelected
                    ? 'bg-white text-blue-600 border-white font-bold'
                    : 'border-gray-300 bg-transparent'
                }`}
              >
                {isSelected ? <Check className="w-3 h-3 stroke-[3]" /> : null}
              </span>
              {t.label}
            </button>
          );
        })}
      </div>
      {selected.length === 0 ? (
        <p className="text-[11px] text-gray-400 italic">No technicians assigned</p>
      ) : selected.length > 1 ? (
        <p className="text-[11px] text-blue-600 font-medium">
          {selected.length} technicians assigned
        </p>
      ) : null}
    </div>
  );
}
